import { Injectable, effect, inject, signal, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { AiSessionService } from '@web/store/page-structure/ai-connector/ai-session.service';
import { LayoutFacade } from '@web/store/layout/layout.facade';

import { resolvePickedEntity, type PickedEntity } from './element-picker.util';

/** Same id `content.component.html` puts on the scrollable canvas wrapper. */
const CANVAS_ELEMENT_ID = 'constructorScrollWrapper';

/**
 * Toggled directly on the hovered canvas element — see `styles.less` for the rule. NOT rendered as
 * a separate `position: fixed` overlay div positioned from a `getBoundingClientRect()` snapshot: an
 * earlier version did exactly that from `ai-chat-drawer.component.html`, and it rendered inside the
 * WRONG box — confirmed live 2026-09-10. `[aiChatDrawer] .drawer-container` in
 * `sp-base-grid-layout.component.less` sets `transform: translate3d(0, 0, 0)` on itself even in its
 * "open" state (an identity transform, there for the slide animation) — and ANY `transform` value
 * other than `none`, including a no-op one, makes that element the CONTAINING BLOCK for every
 * `position: fixed` descendant of it, per the CSS spec. The highlight div was a child of that
 * `.drawer-container` (nested inside the drawer's own template), so its "viewport-relative" fixed
 * coordinates were actually resolved against the drawer panel's own box — landing the highlight
 * inside the drawer, nowhere near the canvas, no matter how correct `getBoundingClientRect()`'s
 * numbers were. Styling the real element directly sidesteps the whole class of bug: no coordinates
 * to get wrong, no ancestor whose CSS this has to keep re-auditing.
 */
const HIGHLIGHT_CLASS = 'ai-chat-picker-hovered';

/**
 * "Pick an element in the preview" mode for the AI chat drawer — §7 of `ai-mcp-plan.md`.
 *
 * `providedIn: 'root'` rather than scoped to the drawer: the canvas the picker listens on
 * (`#constructorScrollWrapper`, in `content.component.html`) lives in a completely different part
 * of the component tree than the drawer itself, so there is no shared ancestor to scope a
 * component-level service to — a root singleton is the natural fit, the same way `AiSessionService`
 * (which this reads) is root-level for the same reason.
 *
 * Listens on `document`, filtered to the canvas by `Element.contains()`, rather than binding a
 * listener onto the canvas element directly — this service has no template of its own and the
 * canvas belongs to a component it must not reach into.
 */
@Injectable({ providedIn: 'root' })
export class ElementPickerService {
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly columnsFacade = inject(ColumnsFacade);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly aiSession = inject(AiSessionService);
    private readonly layoutFacade = inject(LayoutFacade);

    private readonly widgetsEntities = toSignal(this.widgetsFacade.widgetsEntities$, { initialValue: {} });
    private readonly columnsEntities = toSignal(this.columnsFacade.columnsEntities$, { initialValue: {} });
    private readonly blocksEntities = toSignal(this.blocksFacade.blocksEntities$, { initialValue: {} });

    private readonly activeState = signal(false);
    /** Whether picking mode is currently on — drives the toggle button and the crosshair cursor. */
    public readonly active: Signal<boolean> = this.activeState.asReadonly();

    /** The canvas element `HIGHLIGHT_CLASS` is currently applied to, if any — so it can be removed again. */
    private highlightedElement: Element | null = null;

    private readonly pickedState = signal<PickedEntity | null>(null);
    /** Set once, on the click that ends a picking session — the caller must `clearPicked()` after reading it. */
    public readonly picked: Signal<PickedEntity | null> = this.pickedState.asReadonly();

    public constructor() {
        // Auto-cancels the moment a reply starts — the canvas is already non-interactive during an
        // AI session (see `content.component.ts`'s `isAiSessionActive`), so a picker overlaid on top
        // of that would just be dead weight, and worse, could still be "active" with stale state by
        // the time the canvas re-reads and re-renders under it.
        effect(() => {
            if (this.aiSession.isActive() && this.activeState()) {
                this.deactivate();
            }
        });

        effect((onCleanup) => {
            if (!this.activeState()) {
                return;
            }

            const onMouseMove = (event: MouseEvent): void => this.handleMouseMove(event);
            const onClick = (event: MouseEvent): void => this.handleClick(event);
            // Capture phase, and stops propagation on the way out: `BaseDrawerComponent` has its own
            // `window:keydown.esc` listener that CLOSES THE WHOLE DRAWER. Bubble order puts this
            // `document` listener before that `window` one, so without stopping here, Escape would
            // both cancel picking AND close the drawer in the same keypress — cancelling picking
            // should be the ONLY effect.
            const onKeyDown = (event: KeyboardEvent): void => {
                if (event.key !== 'Escape') {
                    return;
                }

                event.stopPropagation();
                event.stopImmediatePropagation();
                this.deactivate();
            };

            // Capture phase on click: picking mode must intercept the click BEFORE it reaches the
            // widget/column/block's own click handlers (e.g. the normal "select this widget" flow) —
            // otherwise picking an element would also open its settings panel underneath the drawer.
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('click', onClick, true);
            document.addEventListener('keydown', onKeyDown, true);

            onCleanup(() => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('click', onClick, true);
                document.removeEventListener('keydown', onKeyDown);
            });
        });
    }

    public toggle(): void {
        if (this.activeState()) {
            this.deactivate();
            return;
        }

        this.activeState.set(true);
        // Same "preview mode" the AI reply blocker drives (`LayoutSelectors.selectIsCanvasPreviewActive`)
        // — the two are ORed together, so whichever is active hides the editing chrome. Also closes
        // whatever settings panel is open: it would otherwise float over a canvas now armed to treat
        // the next click as a pick, not a "select this widget".
        this.layoutFacade.setElementPickerActive(true);
        this.layoutFacade.hideSelectedEntitySettings();
    }

    public cancel(): void {
        this.deactivate();
    }

    public clearPicked(): void {
        this.pickedState.set(null);
    }

    private deactivate(): void {
        this.activeState.set(false);
        this.setHighlighted(null);
        this.layoutFacade.setElementPickerActive(false);
    }

    private handleMouseMove(event: MouseEvent): void {
        this.setHighlighted(this.resolveElementAt(event.target));
    }

    /** No-ops when the element hasn't changed — every mousemove over the same element would otherwise thrash the class. */
    private setHighlighted(element: Element | null): void {
        if (element === this.highlightedElement) {
            return;
        }

        this.highlightedElement?.classList.remove(HIGHLIGHT_CLASS);
        element?.classList.add(HIGHLIGHT_CLASS);
        this.highlightedElement = element;
    }

    private handleClick(event: MouseEvent): void {
        const canvas = document.getElementById(CANVAS_ELEMENT_ID);
        const target = event.target instanceof Element ? event.target : null;

        if (!canvas || !target || !canvas.contains(target)) {
            // Outside the canvas entirely (the drawer itself, the header, …) — leave picking mode
            // on and let the click behave normally; only a canvas click or Escape ends a session.
            return;
        }

        // Stops the click from ALSO reaching the widget/column/block's own handlers underneath —
        // picking an element must not simultaneously select it in the normal editor sense.
        event.preventDefault();
        event.stopPropagation();

        const entity = resolvePickedEntity(
            target,
            {
                isWidgetId: (id) => Boolean(this.widgetsEntities()[id]),
                isColumnId: (id) => Boolean(this.columnsEntities()[id]),
                isBlockId: (id) => Boolean(this.blocksEntities()[id]),
            },
            canvas
        );

        if (entity) {
            this.pickedState.set(entity);
        }

        this.deactivate();
    }

    /** Same resolution `handleClick` uses, but returns the matched DOM element itself — `setHighlighted` needs it, not just its id. */
    private resolveElementAt(target: EventTarget | null): Element | null {
        const canvas = document.getElementById(CANVAS_ELEMENT_ID);
        const element = target instanceof Element ? target : null;

        if (!canvas || !element || !canvas.contains(element)) {
            return null;
        }

        const entity = resolvePickedEntity(
            element,
            {
                isWidgetId: (id) => Boolean(this.widgetsEntities()[id]),
                isColumnId: (id) => Boolean(this.columnsEntities()[id]),
                isBlockId: (id) => Boolean(this.blocksEntities()[id]),
            },
            canvas
        );

        // `[data-raw-id]`, not `#id` / `document.getElementById`: the plain `id` attribute is shared
        // with unrelated concerns (a user-set widget `displayId`, Angular CDK's own `cdkDropList` id)
        // and — confirmed live — a column's template puts `id` on TWO elements at once, so looking up
        // geometry by `id` could resolve to a decoy even once `entity.id` itself is correct.
        // `data-raw-id` is written in exactly one place per entity (see `element-picker.util.ts`),
        // and scoping the query to `canvas` on top of that means this can never draw its highlight
        // off an element outside it, even if some future markup carried a colliding `data-raw-id`.
        return entity ? canvas.querySelector(`[data-raw-id="${entity.id}"]`) : null;
    }
}
